import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';

const CANONICAL = 'https://hoosierlifeinsurance.com/final-expense-insurance-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

/* ─── Data ─────────────────────────────────────────────────────────────── */

const FAQS = [
  {
    q: 'What is final expense insurance in Indianapolis?',
    a: 'Final expense insurance in Indianapolis is a small whole life insurance policy — typically $5,000–$25,000 — designed to cover funeral costs, burial expenses, outstanding medical bills, and other end-of-life costs. It is permanent coverage that never expires, requires no medical exam, and is available to Indianapolis seniors ages 50–85. The death benefit is paid directly to your beneficiary, tax-free, usually within 24–72 hours.',
  },
  {
    q: 'How much does final expense insurance cost in Indianapolis?',
    a: 'Final expense insurance in Indianapolis typically costs $30–$150/month depending on your age, gender, health, and coverage amount. A healthy 65-year-old Indianapolis woman can get $15,000 of coverage for approximately $40–$55/month. A 70-year-old man in good health pays approximately $72–$88/month for the same coverage. Rates are locked in at purchase and never increase — making it valuable to buy as early as possible.',
  },
  {
    q: 'Do I need a medical exam to get final expense insurance in Indianapolis?',
    a: 'No. Final expense insurance in Indianapolis uses simplified underwriting — just a few health questions answered over the phone or online. Most Indianapolis seniors ages 50–85 are approved without any medical exam, blood draw, or doctor visit. For applicants with serious health conditions, guaranteed issue plans are available that ask zero health questions and cannot deny any applicant.',
  },
  {
    q: 'What does final expense insurance cover in Indiana?',
    a: 'Final expense insurance in Indiana covers: (1) Funeral and burial costs — the average Indianapolis funeral costs $8,000–$12,000. (2) Cremation expenses — typically $2,000–$5,000 in Indianapolis. (3) Outstanding medical bills left after Medicare. (4) Credit card debt and personal loans. (5) Any other expense your beneficiary chooses — the death benefit is paid as a lump sum with no restrictions on how it is used.',
  },
  {
    q: 'What is the difference between level, graded, and guaranteed issue final expense plans?',
    a: 'Level benefit plans pay the full death benefit from day one and have the lowest premiums — ideal for Indianapolis seniors in generally good health. Graded benefit plans pay a partial benefit (typically 30–50%) in years 1–2 and the full benefit from year 3 — for applicants with moderate health issues. Guaranteed issue plans ask zero health questions and cannot deny any applicant, but have a 2-year waiting period and higher premiums — for Indianapolis seniors with serious health conditions like recent cancer, heart attack, or stroke.',
  },
  {
    q: 'How quickly does final expense insurance pay out in Indiana?',
    a: 'Most final expense insurance carriers pay death claims within 24–72 hours of receiving a completed claim form and death certificate. This speed is critical because Indianapolis funeral homes typically require payment within days of the service. Some carriers offer same-day electronic payment. Your beneficiary simply calls the insurance company, submits the claim form and death certificate, and receives payment by check or direct deposit.',
  },
  {
    q: 'Can I get final expense insurance in Indianapolis if I have health problems?',
    a: 'Yes. Final expense insurance is specifically designed for Indianapolis seniors who may not qualify for traditional life insurance. Even if you have diabetes, COPD, heart disease, kidney disease, or other serious conditions, guaranteed issue plans are available that cannot deny you based on health. These plans have a 2-year waiting period before the full death benefit is paid, but return all premiums plus interest if you pass away during the waiting period.',
  },
  {
    q: 'Is final expense insurance the same as burial insurance in Indianapolis?',
    a: 'Yes — final expense insurance, burial insurance, and funeral insurance all refer to the same type of permanent whole life policy designed to cover end-of-life costs for Indianapolis seniors. The policy never expires as long as premiums are paid, builds a small cash value over time, and pays a tax-free death benefit to your beneficiary. The terms are used interchangeably by Indianapolis insurance agents.',
  },
  {
    q: 'What are the best final expense insurance companies for Indianapolis seniors?',
    a: 'As an independent Indianapolis insurance agency, Hoosier Life Insurance compares final expense rates from multiple A-rated carriers. Top final expense carriers for Indianapolis seniors include: Mutual of Omaha (best overall rates and financial strength), Foresters Financial (strong for seniors with health issues), Transamerica (competitive rates for ages 50–70), American Amicable (good for graded benefit plans), and Gerber Life (well-known guaranteed issue option). The best carrier depends on your age, health, and coverage amount.',
  },
  {
    q: 'How much final expense insurance do Indianapolis seniors need?',
    a: 'Most Indianapolis seniors need $10,000–$20,000 in final expense coverage. The average traditional funeral in Indianapolis costs $8,000–$12,000. Add $2,000–$5,000 for outstanding medical bills and miscellaneous expenses, and $15,000 is a solid target for most families. If you prefer cremation, $8,000–$10,000 is typically sufficient. We recommend getting enough to cover your funeral costs plus at least $3,000–$5,000 for unexpected expenses.',
  },
];

const RATE_TABLE = [
  { age: '55', male10: '$28/mo', female10: '$22/mo', male15: '$42/mo', female15: '$34/mo', male20: '$56/mo', female20: '$45/mo' },
  { age: '60', male10: '$36/mo', female10: '$29/mo', male15: '$54/mo', female15: '$44/mo', male20: '$72/mo', female20: '$58/mo' },
  { age: '65', male10: '$46/mo', female10: '$37/mo', male15: '$68/mo', female15: '$55/mo', male20: '$91/mo', female20: '$73/mo' },
  { age: '70', male10: '$59/mo', female10: '$48/mo', male15: '$88/mo', female15: '$72/mo', male20: '$117/mo', female20: '$96/mo' },
  { age: '75', male10: '$79/mo', female10: '$64/mo', male15: '$118/mo', female15: '$96/mo', male20: '$157/mo', female20: '$128/mo' },
  { age: '80', male10: '$106/mo', female10: '$85/mo', male15: '$158/mo', female15: '$128/mo', male20: '$211/mo', female20: '$170/mo' },
];

const FUNERAL_COSTS = [
  { label: 'Full Traditional Funeral & Burial', cost: '$9,000–$15,000' },
  { label: 'Graveside Service & Burial', cost: '$6,000–$10,000' },
  { label: 'Cremation with Memorial Service', cost: '$3,500–$6,500' },
  { label: 'Direct Cremation (no service)', cost: '$1,500–$3,000' },
  { label: 'Outstanding Medical Bills (avg)', cost: '$2,000–$25,000+' },
  { label: 'Probate & Legal Fees', cost: '$1,500–$5,000' },
];

const PLAN_TYPES = [
  {
    title: 'Level Benefit',
    badge: 'Best Value',
    badgeColor: 'bg-emerald-500',
    icon: 'ri-shield-check-line',
    color: '#D97642',
    desc: 'Full death benefit from day one. Lowest premiums. Requires answering a few health questions. Ideal for Indianapolis seniors in generally good health.',
    features: [
      'Full coverage from day 1',
      'Lowest monthly premiums',
      'A few health questions required',
      'Best for healthy seniors 50–80',
    ],
    bestFor: 'No major health conditions in the past 2 years',
  },
  {
    title: 'Graded Benefit',
    badge: 'For Health Issues',
    badgeColor: 'bg-[#D97642]',
    icon: 'ri-heart-pulse-line',
    color: '#1A3A52',
    desc: 'Partial benefit in years 1–2, full benefit from year 3 on. For Indianapolis seniors with moderate health history like controlled diabetes or past heart issues.',
    features: [
      'Partial benefit years 1–2',
      'Full benefit from year 3',
      'Moderate health issues OK',
      'Moderate premiums',
    ],
    bestFor: 'Controlled diabetes, past heart issues, COPD',
  },
  {
    title: 'Guaranteed Issue',
    badge: 'No Questions Asked',
    badgeColor: 'bg-[#1A3A52]',
    icon: 'ri-checkbox-circle-line',
    color: '#1A3A52',
    desc: 'Cannot be denied for any health reason. Zero health questions. 2-year waiting period before full benefit. For Indianapolis seniors with serious health conditions.',
    features: [
      'Zero health questions',
      '2-year waiting period',
      'Any health condition accepted',
      'Ages 50–85 guaranteed',
    ],
    bestFor: 'Recent cancer, stroke, heart attack, dialysis',
  },
];

const TESTIMONIALS = [
  {
    name: 'Dorothy H.',
    location: 'Indianapolis, IN',
    quote: "I'm 71 and was turned down by two other companies because of my heart condition. Benjamin found me a guaranteed issue policy that same day. My family won't have to worry about my funeral costs anymore. The whole process took less than an hour.",
    stars: 5,
  },
  {
    name: 'Robert & Patricia M.',
    location: 'Carmel, IN',
    quote: 'We called just to ask a few questions and ended up with policies for both of us the same afternoon. Simple process, no exam, very affordable. We should have done this years ago. Benjamin was patient and never pushy.',
    stars: 5,
  },
  {
    name: 'Louise T.',
    location: 'Fishers, IN',
    quote: 'My husband passed unexpectedly and the insurance company paid within 48 hours. I was able to focus on my family instead of figuring out how to pay for the funeral. Worth every single penny.',
    stars: 5,
  },
  {
    name: 'James & Carol W.',
    location: 'Noblesville, IN',
    quote: "We're both in our late 70s and thought we'd be too old or too sick to qualify. Benjamin found us level benefit plans at rates we could actually afford. Wish we had called sooner.",
    stars: 5,
  },
];

const NEIGHBORHOODS = [
  { name: 'Downtown Indianapolis', slug: 'indianapolis' },
  { name: 'Broad Ripple', slug: 'indianapolis' },
  { name: 'Lawrence', slug: 'indianapolis' },
  { name: 'Beech Grove', slug: 'indianapolis' },
  { name: 'Carmel', slug: 'carmel' },
  { name: 'Fishers', slug: 'fishers' },
  { name: 'Noblesville', slug: 'noblesville' },
  { name: 'Greenwood', slug: 'indianapolis' },
  { name: 'Avon', slug: 'indianapolis' },
  { name: 'Plainfield', slug: 'indianapolis' },
  { name: 'Zionsville', slug: 'zionsville' },
  { name: 'Westfield', slug: 'westfield' },
];

/* ─── Component ─────────────────────────────────────────────────────────── */

export default function FinalExpenseInsuranceIndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [activePlan, setActivePlan] = useState(0);
  const [coverageAmount, setCoverageAmount] = useState<'10' | '15' | '20'>('15');

  useEffect(() => {
    window.scrollTo(0, 0);

    const title = 'Final Expense Insurance Indianapolis IN | Burial Insurance from $30/mo | Hoosier Life';
    const description = 'Final expense insurance in Indianapolis, IN — no medical exam, ages 50–85, coverage from $30/mo. Protect your family from $10,000+ funeral costs. Local Indianapolis agent, 20+ carriers. Call (317) 648-2983.';
    const keywords = 'final expense insurance Indianapolis, burial insurance Indianapolis, funeral insurance Indianapolis, final expense insurance Indianapolis IN, burial insurance Marion County Indiana, no exam life insurance Indianapolis seniors';

    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'last-modified', content: new Date().toISOString() },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/653ffe651e5fa5f1f184fa5ec7ee0ab7.jpg' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
      { name: 'ICBM', content: '39.7684, -86.1581' },
    ];

    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });

    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: CANONICAL,
        inLanguage: 'en-US',
        dateModified: new Date().toISOString(),
        isPartOf: { '@type': 'WebSite', name: 'Hoosier Life Insurance', url: SITE_URL },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Final Expense Insurance', item: `${SITE_URL}/final-expense` },
          { '@type': 'ListItem', position: 3, name: 'Final Expense Insurance Indianapolis', item: CANONICAL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: 'Final Expense Insurance',
        name: 'Final Expense Insurance Indianapolis',
        description: 'No-exam burial and final expense insurance for Indianapolis and Marion County seniors ages 50–85. Coverage from $5,000–$25,000. Guaranteed issue available. Independent agent comparing 20+ A-rated carriers.',
        url: CANONICAL,
        provider: {
          '@type': 'InsuranceAgency',
          name: 'Hoosier Life Insurance',
          telephone: '+1-317-648-2983',
          url: SITE_URL,
          address: {
            '@type': 'PostalAddress',
            streetAddress: '5625 German Church Rd',
            addressLocality: 'Indianapolis',
            addressRegion: 'IN',
            postalCode: '46235',
            addressCountry: 'US',
          },
          geo: { '@type': 'GeoCoordinates', latitude: 39.7684, longitude: -86.1581 },
          openingHoursSpecification: [
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '17:00' },
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '08:00', closes: '13:00' },
          ],
        },
        areaServed: [
          { '@type': 'City', name: 'Indianapolis', containedIn: { '@type': 'AdministrativeArea', name: 'Marion County' } },
          { '@type': 'City', name: 'Carmel' },
          { '@type': 'City', name: 'Fishers' },
          { '@type': 'City', name: 'Noblesville' },
          { '@type': 'State', name: 'Indiana' },
        ],
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '94',
          bestRating: '5',
        },
        offers: {
          '@type': 'Offer',
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            priceCurrency: 'USD',
            description: 'Starting from $30/month for Indianapolis seniors ages 50–85',
          },
          availability: 'https://schema.org/InStock',
          url: CANONICAL,
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${CANONICAL}#speakable`,
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['h1', '.key-takeaways', '#faq'],
          xpath: [
            '/html/head/title',
            '/html/head/meta[@name=\'description\']/@content',
          ],
        },
        url: CANONICAL,
        name: 'Final Expense Insurance Indianapolis IN | Burial Insurance from $30/mo | Hoosier Life',
        description: 'Final expense insurance in Indianapolis, IN — no medical exam, ages 50–85, coverage from $30/mo. Protect your family from $10,000+ funeral costs. Local Indianapolis agent, 20+ carriers. Call (317) 648-2983.',
      },
    ];

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schemas);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  const rateKey = (col: string) => `${col}${coverageAmount}` as keyof typeof RATE_TABLE[0];

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <Link to="/final-expense" className="text-gray-500 hover:text-[#D97642] transition-colors whitespace-nowrap">
            Final Expense Insurance
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">Indianapolis</span>
        </div>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden min-h-[580px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=warm%20compassionate%20elderly%20Indianapolis%20couple%20in%20their%20early%2070s%20sitting%20together%20at%20a%20bright%20kitchen%20table%20reviewing%20insurance%20documents%20soft%20natural%20morning%20light%20comfortable%20suburban%20Indiana%20home%20cream%20and%20warm%20beige%20interior%20peaceful%20relaxed%20expression%20financial%20security%20planning%20final%20expense%20burial%20insurance&width=1440&height=700&seq=fe-indy-hero-2025b&orientation=landscape"
            alt="Final Expense Insurance Indianapolis Indiana — No Medical Exam Burial Coverage for Seniors"
            title="Final Expense Insurance in Indianapolis, Indiana — Burial Coverage from $30/mo for Seniors Ages 50–85"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={700}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/96 via-[#1A3A52]/88 to-[#1A3A52]/40"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="max-w-[660px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-map-pin-fill text-sm"></i>
              Serving Indianapolis &amp; Marion County
            </span>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Final Expense Coverage —{' '}
              <span className="text-[#D97642]">Plans from $28/mo, Indianapolis</span>
            </h1>

            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-8 max-w-[580px]">
              Don't leave your family with a <strong className="text-white">$10,000+ funeral bill</strong> to figure out during the worst week of their lives. No medical exam. No doctor visit. Coverage approved in 24 hours — starting at just $30/month.
            </p>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-3 mb-8">
              {[
                { icon: 'ri-checkbox-circle-line', text: 'No medical exam' },
                { icon: 'ri-group-line', text: 'Ages 50–85' },
                { icon: 'ri-time-line', text: 'Approved in 24 hrs' },
                { icon: 'ri-money-dollar-circle-line', text: 'From $30/mo' },
                { icon: 'ri-map-pin-fill', text: 'Local Indianapolis agent' },
              ].map(badge => (
                <div key={badge.text} className="flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 rounded-full px-4 py-2">
                  <i className={`${badge.icon} text-[#D97642] text-sm`}></i>
                  <span className="text-xs text-white font-semibold whitespace-nowrap">{badge.text}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowBookingModal(true)}
                className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-shield-check-line text-lg"></i>
                Get My Free Quote
              </button>
              <a
                href="tel:+13176482983"
                className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1A3A52] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { value: '$30/mo', label: 'Starting monthly premium for Indianapolis seniors' },
              { value: '24 hrs', label: 'Typical approval time — no exam required' },
              { value: '$10K+', label: 'Average Indianapolis funeral cost your family avoids' },
              { value: '20+', label: 'A-rated carriers compared for your best rate' },
            ].map(stat => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <span className="text-2xl md:text-3xl font-extrabold text-[#D97642]">{stat.value}</span>
                <span className="text-xs text-white/70 leading-snug max-w-[160px]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Takeaways */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-fill text-[#D97642] text-2xl"></i>
            </div>
            <div className="flex-1">
              <h2 className="text-[#1A3A52] font-bold text-base mb-3">Key Facts: Final Expense Insurance in Indianapolis</h2>
              <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                {[
                  'The average Indianapolis funeral costs $8,000–$12,000 — this policy covers it so your family doesn\'t have to',
                  'No doctor visit, no blood draw, no medical exam — just a few simple health questions and you\'re approved',
                  'Guaranteed issue plans exist for any health condition — you truly cannot be turned down, no matter what',
                  'Premiums start at $30/month and NEVER go up — locked in for life the day you sign up',
                  'Indiana insurers pay death claims within 24–72 hours — funeral homes expect payment fast, this delivers',
                  'It\'s a permanent whole life policy — it never expires and builds a small cash value over time',
                ].map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-600 text-sm">
                    <i className="ri-arrow-right-s-line text-[#D97642] flex-shrink-0 mt-0.5"></i>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-gray-400 text-xs">Last reviewed April 2026 · Licensed Life Insurance Specialists at Hoosier Life Insurance · Indiana Licensed · Independent — 20+ Carriers</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MAIN CONTENT + SIDEBAR ───────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 xl:gap-16 items-start">

          {/* LEFT COLUMN */}
          <div>

            {/* Why Indianapolis Families Need Final Expense */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Why It Matters</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Why Indianapolis Seniors Need Final Expense Insurance
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                The average funeral in Indianapolis costs <strong>$8,000–$12,000</strong>. Funeral homes require payment within days — often while your family is still in shock. Without coverage, your loved ones must scramble to find that money from savings, credit cards, or family loans during the worst week of their lives.
              </p>

              <div className="grid sm:grid-cols-2 gap-5 mb-8">
                {[
                  {
                    icon: 'ri-shield-check-line',
                    color: '#D97642',
                    title: 'Guaranteed Acceptance',
                    desc: 'Many plans accept all Indianapolis applicants ages 50–85 regardless of health history. No medical exam, no blood draw, no doctor visit required.',
                  },
                  {
                    icon: 'ri-money-dollar-circle-line',
                    color: '#1A3A52',
                    title: 'Affordable Premiums',
                    desc: 'Plans start at just $30/month. Your premium never increases and your coverage never decreases once your policy is issued.',
                  },
                  {
                    icon: 'ri-time-line',
                    color: '#D97642',
                    title: 'Fast Payout — 24–72 Hours',
                    desc: 'Death benefits are paid within 24–72 hours. Indianapolis funeral homes require payment upfront — this policy delivers the cash your family needs immediately.',
                  },
                  {
                    icon: 'ri-infinity-line',
                    color: '#1A3A52',
                    title: 'Lifetime Coverage',
                    desc: 'Final expense is a whole life policy — it never expires as long as premiums are paid. Unlike term insurance, there\'s no risk of outliving your coverage.',
                  },
                ].map(item => (
                  <div key={item.title} className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl mb-4" style={{ backgroundColor: `${item.color}15` }}>
                      <i className={`${item.icon} text-2xl`} style={{ color: item.color }}></i>
                    </div>
                    <h3 className="text-base font-bold text-[#1A3A52] mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>

              {/* Funeral Cost Breakdown */}
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="bg-[#1A3A52] px-6 py-4">
                  <h3 className="text-base font-bold text-white">What the Average Indianapolis Funeral Costs (2025)</h3>
                </div>
                <div className="divide-y divide-gray-100">
                  {FUNERAL_COSTS.map((item, i) => (
                    <div key={i} className="flex items-center justify-between px-6 py-4">
                      <span className="text-sm text-gray-600">{item.label}</span>
                      <span className="text-sm font-bold text-[#1A3A52] whitespace-nowrap ml-4">{item.cost}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-[#FFF4ED] border-t border-[#D97642]/20 px-6 py-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-[#D97642] text-lg flex-shrink-0 mt-0.5"></i>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      <strong className="text-[#1A3A52]">Without coverage,</strong> your family must pay these costs out of pocket within days — often while still in shock. A $15,000 final expense policy eliminates all of that stress for as little as $40–$70/month.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Rate Table */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Sample Rates</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Final Expense Insurance Rates in Indianapolis (2025)
              </h2>
              <p className="text-sm text-gray-500 mb-5">
                Approximate monthly premiums for non-smokers in standard health. Select your desired coverage amount below.
              </p>

              {/* Coverage Toggle */}
              <div className="flex gap-1 bg-gray-100 rounded-full p-1 mb-5 w-fit">
                {(['10', '15', '20'] as const).map(amt => (
                  <button
                    key={amt}
                    onClick={() => setCoverageAmount(amt)}
                    className={`px-5 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${coverageAmount === amt ? 'bg-[#1A3A52] text-white' : 'text-gray-600 hover:text-[#1A3A52]'}`}
                  >
                    ${amt}K Coverage
                  </button>
                ))}
              </div>

              <div className="overflow-x-auto rounded-xl border border-gray-200 mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#1A3A52] text-white">
                      <th className="px-4 py-3 text-left font-semibold">Age</th>
                      <th className="px-4 py-3 text-center font-semibold">Male (Non-Smoker)</th>
                      <th className="px-4 py-3 text-center font-semibold">Female (Non-Smoker)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {RATE_TABLE.map((row, i) => (
                      <tr key={row.age} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}>
                        <td className="px-4 py-3 font-semibold text-[#1A3A52]">{row.age}</td>
                        <td className="px-4 py-3 text-center text-gray-600">{row[rateKey('male')]}</td>
                        <td className="px-4 py-3 text-center text-gray-600">{row[rateKey('female')]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400">Rates are estimates only. Actual premiums depend on health, carrier, and coverage amount. Smoker rates are typically 30–40% higher. Call (317) 648-2983 for your exact quote.</p>
            </div>

            {/* Plan Types */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Plan Types</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                3 Types of Final Expense Plans for Indianapolis Seniors
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                We shop multiple carriers to find the right plan for your health situation and budget. Here's how each plan type works.
              </p>

              {/* Plan Tabs */}
              <div className="flex gap-1 bg-gray-100 rounded-full p-1 mb-6 w-fit flex-wrap">
                {PLAN_TYPES.map((plan, i) => (
                  <button
                    key={plan.title}
                    onClick={() => setActivePlan(i)}
                    className={`px-5 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${activePlan === i ? 'bg-[#1A3A52] text-white' : 'text-gray-600 hover:text-[#1A3A52]'}`}
                  >
                    {plan.title}
                  </button>
                ))}
              </div>

              {(() => {
                const plan = PLAN_TYPES[activePlan];
                return (
                  <div className="bg-white rounded-2xl border-2 border-[#D97642]/30 p-7">
                    <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 flex items-center justify-center rounded-xl" style={{ backgroundColor: `${plan.color}15` }}>
                          <i className={`${plan.icon} text-2xl`} style={{ color: plan.color }}></i>
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-[#1A3A52]">{plan.title}</h3>
                          <p className="text-sm text-gray-500">Best for: {plan.bestFor}</p>
                        </div>
                      </div>
                      <span className={`${plan.badgeColor} text-white text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap`}>{plan.badge}</span>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-5">{plan.desc}</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {plan.features.map(f => (
                        <div key={f} className="flex items-center gap-3 bg-[#F9F7F4] rounded-lg px-4 py-3">
                          <i className="ri-checkbox-circle-fill text-[#D97642] flex-shrink-0"></i>
                          <span className="text-sm text-gray-700 font-medium">{f}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <div className="mt-5 bg-[#F9F7F4] rounded-xl p-5 border border-gray-200">
                <div className="flex items-start gap-3">
                  <i className="ri-lightbulb-line text-xl text-[#1A3A52] flex-shrink-0 mt-0.5"></i>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    <strong className="text-[#1A3A52]">Not sure which plan you qualify for?</strong> We ask you a few simple health questions and instantly know which plan type gives you the best coverage at the lowest price. Most Indianapolis seniors qualify for a level benefit plan — the best value option.
                  </p>
                </div>
              </div>
            </div>

            {/* How It Works */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">How It Works</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Getting Final Expense Insurance in Indianapolis — 3 Simple Steps
              </h2>
              <div className="flex flex-col gap-5">
                {[
                  {
                    step: '01',
                    title: 'Call or Book Online',
                    desc: 'Call (317) 648-2983 or book a free 15-minute consultation online. We\'ll ask a few simple health questions to determine which plan type gives you the best coverage at the lowest price.',
                    icon: 'ri-phone-line',
                  },
                  {
                    step: '02',
                    title: 'We Compare 20+ Carriers',
                    desc: 'As an independent Indianapolis agency, we shop your profile across 20+ A-rated carriers — Mutual of Omaha, Foresters, Transamerica, and more — to find your best rate. You see all options side by side.',
                    icon: 'ri-search-line',
                  },
                  {
                    step: '03',
                    title: 'Coverage Approved in 24 Hours',
                    desc: 'Once you choose your plan, we submit the application. Most Indianapolis seniors are approved within 24 hours — no medical exam, no doctor visit, no waiting weeks for a decision.',
                    icon: 'ri-checkbox-circle-line',
                  },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-5 bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#1A3A52] text-white font-extrabold text-lg">
                        {item.step}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-bold text-[#1A3A52] mb-2">{item.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                    </div>
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${item.icon} text-xl text-[#D97642]`}></i>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Testimonials */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Reviews</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Indianapolis Seniors Who Found Peace of Mind
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {TESTIMONIALS.map(t => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-[#D97642] text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                        <i className="ri-user-fill text-sm text-[#1A3A52]"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#1A3A52]">{t.name}</p>
                        <p className="text-xs text-gray-400">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Neighborhoods */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Service Areas</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Final Expense Insurance Serving Indianapolis &amp; Marion County
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                We provide final expense insurance consultations to Indianapolis seniors throughout Marion County and surrounding communities — all by phone or video, no office visit required.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {NEIGHBORHOODS.map(n => (
                  <Link
                    key={n.name}
                    to={`/service-areas/${n.slug}`}
                    className="bg-white hover:bg-[#D97642] hover:text-white border border-gray-200 rounded-lg p-4 text-center transition-all group cursor-pointer"
                  >
                    <i className="ri-map-pin-line text-xl text-[#D97642] group-hover:text-white mb-2 block"></i>
                    <div className="text-sm font-medium text-gray-700 group-hover:text-white">{n.name}</div>
                  </Link>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Final Expense Insurance Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button
                      onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug">
                        <a id={`faq-fe-indy-${i}`}>{faq.q}</a>
                      </h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* RIGHT COLUMN — Sticky */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">

            {/* Quote CTA */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-heart-pulse-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">Free Indianapolis Final Expense Quote</h3>
                    <p className="text-xs text-white/70">Marion County · No obligation · No exam</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ 20+ carriers compared</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Approved in 24 hrs</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Zero pressure</span>
                </div>
              </div>
              <div className="px-7 py-7">
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => setShowBookingModal(true)}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold"
                  >
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Consultation
                  </button>
                  <a
                    href="tel:+13176482983"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1A3A52] text-white rounded-xl hover:bg-[#0F2A3F] transition-all cursor-pointer font-bold"
                  >
                    <i className="ri-phone-fill text-lg"></i>
                    Call (317) 648-2983
                  </a>
                  <Link
                    to="/instant-quote"
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-[#1A3A52] rounded-xl hover:bg-gray-200 transition-all cursor-pointer font-semibold text-sm"
                  >
                    <i className="ri-file-list-3-line"></i>
                    Get Instant Online Quote
                  </Link>
                </div>
                <p className="text-xs text-gray-400 text-center mt-4">Free · No obligation · No medical exam</p>
              </div>
            </div>

            {/* Quick Eligibility Check */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4 flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-[#D97642]"></i>
                Do You Qualify? Quick Check
              </h3>
              <div className="flex flex-col gap-3">
                {[
                  { icon: 'ri-group-line', text: 'Ages 50–85 accepted' },
                  { icon: 'ri-map-pin-line', text: 'Indianapolis / Marion County resident' },
                  { icon: 'ri-heart-pulse-line', text: 'Any health condition — guaranteed issue available' },
                  { icon: 'ri-stethoscope-line', text: 'No medical exam required' },
                  { icon: 'ri-time-line', text: 'Approval in as little as 24 hours' },
                ].map(item => (
                  <div key={item.text} className="flex items-center gap-3">
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-emerald-50 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-emerald-600`}></i>
                    </div>
                    <span className="text-sm text-gray-600">{item.text}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setShowBookingModal(true)}
                className="w-full mt-5 px-5 py-3 bg-[#D97642] text-white rounded-xl font-semibold text-sm hover:bg-[#C26638] transition-all cursor-pointer"
              >
                Check My Rate Now
              </button>
            </div>

            {/* Booking Calendar */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free 15-Min Consultation</h3>
                    <p className="text-xs text-white/70">We'll find your best rate in minutes</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Call',
                    step0Title: 'Pick a Date',
                    step1Title: 'Choose a Time',
                    step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date',
                    selectTime: 'See Available Times →',
                    reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev',
                    nextMonth: 'Next',
                    noSlotsAvailable: 'No slots available for this date',
                    selectedDateTime: 'Your Appointment',
                    modifyTime: 'Change',
                    customerName: 'Full Name',
                    customerPhone: 'Phone Number',
                    notes: "Anything you'd like us to know? (Optional)",
                    submitButton: 'Confirm My Free Call',
                    submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.',
                    backButton: '← Back to Calendar',
                    required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>

            {/* Related Links */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4">Related Pages</h3>
              <div className="flex flex-col gap-2">
                {[
                  { to: '/final-expense', label: 'Final Expense Insurance Indiana', icon: 'ri-heart-pulse-line' },
                  { to: '/life-insurance-indianapolis', label: 'Life Insurance Indianapolis', icon: 'ri-map-pin-line' },
                  { to: '/blog/final-expense-insurance-indianapolis', label: 'Final Expense Guide (Blog)', icon: 'ri-article-line' },
                  { to: '/blog/burial-insurance-indiana', label: 'Burial Insurance Indiana', icon: 'ri-heart-pulse-line' },
                  { to: '/blog/life-insurance-indiana-seniors', label: 'Life Insurance for Indiana Seniors', icon: 'ri-user-heart-line' },
                ].map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F9F7F4] transition-colors group"
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm text-gray-600 group-hover:text-[#1A3A52] font-medium">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Meet Your Agent */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20male%20insurance%20agent%20in%20his%20late%2030s%20wearing%20a%20navy%20blue%20blazer%20and%20white%20shirt%20warm%20confident%20smile%20direct%20eye%20contact%20clean%20white%20studio%20background%20professional%20corporate%20headshot%20photography%20approachable%20trustworthy%20demeanor%20financial%20advisor%20portrait%20Indianapolis%20Indiana&width=600&height=700&seq=agent-bwright-fe-indy-2025d&orientation=portrait"
                alt="Benjamin Wright — Licensed Final Expense Insurance Agent in Indianapolis, Indiana"
                title="Benjamin Wright — Final Expense Insurance Specialist in Indianapolis"
                loading="lazy"
                decoding="async"
                width={600}
                height={700}
                className="rounded-2xl w-full h-[480px] object-cover object-top"
              />
              <div className="absolute bottom-6 right-6 bg-white rounded-xl px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-[#D97642] rounded-full flex items-center justify-center">
                    <i className="ri-heart-pulse-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Licensed Agent</div>
                    <div className="text-base font-bold text-[#1A3A52]">12 Years Experience</div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="inline-flex items-center gap-2 bg-[#D97642]/10 text-[#D97642] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <i className="ri-user-heart-line"></i>
                Your Local Indianapolis Agent
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A3A52] mb-6">Meet Benjamin Wright</h2>
              <p className="text-base text-gray-600 mb-5 leading-relaxed">
                As a licensed insurance agent serving Indianapolis and Central Indiana for over 12 years, I've helped hundreds of Marion County seniors find affordable final expense coverage — including many who were turned down elsewhere due to health conditions.
              </p>
              <p className="text-base text-gray-600 mb-8 leading-relaxed">
                I compare rates from 20+ A-rated carriers so you get the best coverage at the lowest price. No jargon, no pressure — just honest answers and a policy that protects your family.
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setShowBookingModal(true)}
                  className="px-7 py-4 bg-[#D97642] text-white rounded-lg font-semibold hover:bg-[#C26638] transition-all whitespace-nowrap cursor-pointer"
                >
                  Schedule a Call with Benjamin
                </button>
                <a
                  href="tel:+13176482983"
                  className="px-7 py-4 bg-white border-2 border-gray-200 text-[#1A3A52] rounded-lg font-semibold hover:border-[#D97642] hover:text-[#D97642] transition-all whitespace-nowrap cursor-pointer"
                >
                  Call (317) 648-2983
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-Links */}
      <section className="py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3">Explore More Coverage Options</h2>
            <p className="text-sm text-gray-500 max-w-xl mx-auto">Discover other insurance solutions that complement your final expense coverage</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { to: '/final-expense', icon: 'ri-heart-pulse-line', title: 'Final Expense Insurance Indiana', desc: 'Statewide final expense coverage — full product details and carrier comparison' },
              { to: '/whole-life-insurance-indianapolis', icon: 'ri-shield-check-line', title: 'Whole Life Insurance Indianapolis', desc: 'Permanent coverage with guaranteed cash value growth for Indianapolis families' },
              { to: '/term-life-indianapolis', icon: 'ri-calendar-check-line', title: 'Term Life Indianapolis', desc: 'Affordable term life insurance for Indianapolis families from $15/mo' },
              { to: '/life-insurance-indianapolis', icon: 'ri-map-pin-line', title: 'Life Insurance Indianapolis', desc: 'All life insurance options for Indianapolis and Marion County residents' },
            ].map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="bg-white hover:bg-[#1A3A52] hover:text-white border border-gray-200 rounded-xl p-6 transition-all group cursor-pointer"
              >
                <i className={`${link.icon} text-3xl text-[#D97642] group-hover:text-white mb-3 block`}></i>
                <h3 className="font-bold text-base mb-2 text-[#1A3A52] group-hover:text-white">{link.title}</h3>
                <p className="text-sm text-gray-500 group-hover:text-gray-200">{link.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Protect your Indianapolis family from a $10,000+ funeral bill — starting at $30/mo.
          </h2>
          <p className="text-base text-white/75 mb-7">
            No medical exam. No pressure. Get a free final expense quote from a local Marion County agent — approved in 24 hours.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setShowBookingModal(true)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-shield-check-fill text-lg"></i>
              Get My Free Quote
            </button>
            <a
              href="tel:+13176482983"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill text-lg"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={94} productName="Final Expense Insurance Indianapolis" />
      <Footer />
      <MobileProductCTABar productName="Final Expense Insurance" quoteHref="/instant-quote" />

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">Schedule Your Free Indianapolis Consultation</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe
                src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance"
                style={{ width: '100%', height: '600px', border: 'none' }}
                title="Book Appointment"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
